import { googleLogout } from "@react-oauth/google";
import { createAsyncThunk, createSlice, ThunkDispatch } from "@reduxjs/toolkit";
import axios from "axios";
import { apolloClient } from "../../apollo/graphql";
import {
  CHANGE_PASSWORD,
  FORCE_CHANGE_PASSWORD,
  FORGOT_PASSWORD,
  RESEND_VERIFICATION_EMAIL,
  RESET_USER_PASSWORD,
  SIGN_IN,
  SIGN_IN_CAPTCHA,
  SIGN_IN_FACEBOOK,
  SIGN_IN_GOOGLE,
  SIGN_UP,
  SIGN_UP_CAPTCHA,
  SINGLE_UPLOAD_PROFILE,
  VERIFY_EMAIL,
} from "../../apollo/mutations";

import { RootState } from "../../app/store";
import {
  RejectWithValueType,
  AuthState,
  AuthUser,
  ForgotAuth,
  NewUser,
  ResetAuth,
  UpdatePassword,
  UserCredentials,
  VerifyAuth,
  VerifyResendAuth,
  UploadGraphQL,
  UploadGraphQLResult,
  RecaptchaRegister,
} from "./types/authType";

export const signInApollo = createAsyncThunk<
  any,
  UserCredentials,
  { rejectValue: RejectWithValueType }
>("auth/signIn", async (authUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { email, password } = authUser;

  try {
    const response = await apolloClient.mutate({
      mutation: SIGN_IN,
      variables: { email: email, password: password },
    });

    if (response && response.data && response.data.authUser) {
      localStorage.setItem("userInfo", JSON.stringify(response.data.authUser));
      return response.data.authUser as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const signInRecaptcha = createAsyncThunk<
  any,
  UserCredentials,
  { rejectValue: RejectWithValueType }
>("auth/signInRecaptcha", async (authUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { email, password, captcha } = authUser;

  try {
    const response = await apolloClient.mutate({
      mutation: SIGN_IN_CAPTCHA,
      variables: { email: email, password: password, captcha: captcha },
    });

    if (response && response.data && response.data.authUserCaptcha) {
      localStorage.setItem(
        "userInfo",
        JSON.stringify(response.data.authUserCaptcha)
      );
      return response.data.authUserCaptcha as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const updateProfile = createAsyncThunk<
  any,
  AuthUser,
  { rejectValue: RejectWithValueType }
>("auth/updateProfile", async (editProfile, thunkAPI) => {
  const { getState, rejectWithValue, dispatch } = thunkAPI;
  const {
    auth: { user },
  } = getState() as { auth: AuthState };

  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user!.token}`,
      },
    };

    const { data } = await axios.post(
      "/api/users/profile/edit",
      editProfile,
      config
    );
    await setSuccessAction(dispatch, {
      message: "Profile updated successfully",
    });
    return data;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const changePassword = createAsyncThunk<
  any,
  UpdatePassword,
  { rejectValue: RejectWithValueType }
>("auth/changePassword", async (changePass, thunkAPI) => {
  const { getState, rejectWithValue, dispatch } = thunkAPI;
  const { oldPassword, password, confirmPassword } = changePass;
  const {
    auth: { user },
  } = getState() as { auth: AuthState };
  try {
    const response = await apolloClient.mutate({
      mutation: CHANGE_PASSWORD,
      variables: { userId: user?._id, oldPassword, password, confirmPassword },
    });

    if (response && response.data && response.data.changePassword) {
      await setSuccessAction(dispatch, {
        message: "Password changed successfully",
      });
      return response.data.changePassword as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

// Forced change for users blocked by `mustChangePassword`. Uses the unguarded
// backend mutation and returns a fresh auth user (flag cleared, new token).
export const forceChangePassword = createAsyncThunk<
  any,
  UpdatePassword,
  { rejectValue: RejectWithValueType }
>("auth/forceChangePassword", async (changePass, thunkAPI) => {
  const { getState, rejectWithValue, dispatch } = thunkAPI;
  const { oldPassword, password, confirmPassword } = changePass;
  const {
    auth: { user },
  } = getState() as { auth: AuthState };
  try {
    const response = await apolloClient.mutate({
      mutation: FORCE_CHANGE_PASSWORD,
      variables: { userId: user?._id, oldPassword, password, confirmPassword },
    });

    if (response && response.data && response.data.forceChangePassword) {
      localStorage.setItem(
        "userInfo",
        JSON.stringify(response.data.forceChangePassword)
      );
      await setSuccessAction(dispatch, {
        message: "Password changed successfully",
      });
      return response.data.forceChangePassword as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export type GoogleIdToken = {
  idToken: string;
};

export const google = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("auth/google", async (idToken, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  //const { idToken } = res;
  try {
    const response = await apolloClient.mutate({
      mutation: SIGN_IN_GOOGLE,
      variables: { idToken: idToken },
    });
    if (response && response.data && response.data.googleLogin) {
      localStorage.setItem(
        "userInfo",
        JSON.stringify(response.data.googleLogin)
      );
      return response.data.googleLogin as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export type FacebookIdToken = {
  userID: string;
  accessToken: string;
};
export const facebook = createAsyncThunk<
  any,
  FacebookIdToken,
  { rejectValue: RejectWithValueType }
>("auth/facebook", async (res, thunkAPI) => {
  //console.log(res);
  const { rejectWithValue, dispatch } = thunkAPI;
  const { userID, accessToken } = res;
  try {
    const response = await apolloClient.mutate({
      mutation: SIGN_IN_FACEBOOK,
      variables: { userID, accessToken },
    });

    if (response && response.data && response.data.authUser) {
      localStorage.setItem("userInfo", JSON.stringify(response.data.authUser));
      return response.data.authUser as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const forgot = createAsyncThunk<
  any,
  ForgotAuth,
  { rejectValue: RejectWithValueType }
>("auth/forgot", async (authUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { email } = authUser;
  try {
    const response = await apolloClient.mutate({
      mutation: FORGOT_PASSWORD,
      variables: { email: email },
    });

    if (response && response.data && response.data.forgotPassword) {
      await setSuccessAction(dispatch, {
        message: "login reset link email sent",
      });
      return response.data.forgotPassword as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const reset = createAsyncThunk<
  any,
  ResetAuth,
  { rejectValue: RejectWithValueType }
>("auth/reset", async (authUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { password, confirmPassword, id, token } = authUser;

  try {
    const response = await apolloClient.mutate({
      mutation: RESET_USER_PASSWORD,
      variables: { id, token, password, confirmPassword },
    });

    if (response && response.data && response.data.resetUserPassword) {
      await setSuccessAction(dispatch, {
        message: "email reset successfully",
      });
      return response.data.resetUserPassword as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const verify = createAsyncThunk<
  any,
  VerifyAuth,
  { rejectValue: RejectWithValueType }
>("auth/verify", async (authUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { expires, id, token, signature } = authUser;
  try {
    const response = await apolloClient.mutate({
      mutation: VERIFY_EMAIL,
      variables: { id, token, expires, signature },
    });

    if (response && response.data && response.data.verifyEmail) {
      await setSuccessAction(dispatch, {
        message: "Email verified successfully",
      });
      return response.data.verifyEmail as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const resend = createAsyncThunk<
  any,
  VerifyResendAuth,
  { rejectValue: RejectWithValueType }
>("auth/resend", async (authUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { id } = authUser;
  try {
    const response = await apolloClient.mutate({
      mutation: RESEND_VERIFICATION_EMAIL,
      variables: { id },
    });

    if (response && response.data && response.data.resendVerificationEmail) {
      await setSuccessAction(dispatch, {
        message: "Email verification successfully resent",
      });
      return response.data.resendVerificationEmail as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const signUp = createAsyncThunk<
  any,
  NewUser,
  { rejectValue: RejectWithValueType }
>("auth/signUp", async (newUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { email, name, password, confirmPassword } = newUser;

  try {
    const response = await apolloClient.mutate({
      mutation: SIGN_UP,
      variables: {
        email: email,
        name: name,
        password: password,
        confirmPassword: confirmPassword,
      },
    });

    if (response && response.data && response.data.authUser) {
      localStorage.setItem("userInfo", JSON.stringify(response.data.authUser));
      await setSuccessAction(dispatch, {
        message: "Registration successful",
      });
      return response.data.authUser as AuthUser;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const signUpRecaptcha = createAsyncThunk<
  any,
  RecaptchaRegister,
  { rejectValue: RejectWithValueType }
>("auth/signUpRecaptcha", async (captchaUser, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { email, name, country, phone, message, captcha } = captchaUser;

  try {
    const telephone = (country?.phone as string) + phone;
    const response = await apolloClient.mutate({
      mutation: SIGN_UP_CAPTCHA,
      variables: {
        email: email,
        name: name,
        country: country?.label as string,
        phone: telephone,
        message: message,
        captcha: captcha,
      },
    });

    if (response && response.data && response.data.recaptchaRegister) {
      const recap = (await response.data.recaptchaRegister) as AuthUser;
      await setSuccessAction(dispatch, {
        message: recap.email,
      });
      return {
        message:
          "Your data has been submitted!,We will get back to you soon. Have a great day!",
      };
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const uploadProfilePicture = createAsyncThunk<
  any,
  UploadGraphQL,
  { rejectValue: RejectWithValueType }
>("auth/uploadProfilePicture", async (uploadInput, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { _id, avatar, type } = uploadInput;
  //console.log(uploadInput);
  try {
    if (avatar.size > 1000000) {
      const message = "File size can't exceeds 1Mb";
      await setErrorAction(dispatch, { message });
      return rejectWithValue({ message });
    } else {
      const response = await apolloClient.mutate({
        mutation: SINGLE_UPLOAD_PROFILE,
        variables: {
          _id: _id,
          avatar: avatar,
          type: type,
        },
      });

      if (response && response.data && response.data.uploadProfilePicture) {
        await setSuccessAction(dispatch, {
          message: "Upload successful",
        });
        return response.data.uploadProfilePicture as UploadGraphQLResult;
      }
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const uploadFile = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("auth/uploadImage", async (image, thunkAPI) => {
  const { getState, rejectWithValue, dispatch } = thunkAPI;
  const {
    auth: { user },
  } = getState() as { auth: AuthState };

  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user!.token}`,
      },
    };

    const { data } = await axios.post("/api/uploads", image, config);
    await setSuccessAction(dispatch, {
      message: "File uploaded successfully",
    });
    //const pr = await profile(user!.id)
    //console.log(data)
    return data;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const uploadFileMulter = createAsyncThunk<
  any,
  File,
  { rejectValue: RejectWithValueType }
>("auth/uploadImage", async (image, thunkAPI) => {
  const { getState, rejectWithValue, dispatch } = thunkAPI;
  const {
    auth: { user },
  } = getState() as { auth: AuthState };

  try {
    const formData = new FormData();
    formData.append("id", user?._id?.toString() as string);
    formData.append("image", image);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${user!.token}`,
      },
    };

    const { data } = await axios.post("/api/uploads", formData, config);

    return data;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const deleteFile = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("auth/deleteFile", async (fileName, thunkAPI) => {
  const { getState, rejectWithValue, dispatch } = thunkAPI;

  const {
    auth: { user },
  } = getState() as { auth: AuthState };

  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user!.token}`,
      },
    };

    const { data } = await axios.delete(
      `/api/uploads?fileName=${fileName.substring(fileName.lastIndexOf("/"))}`,
      config
    );

    return data;
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

async function setSuccessAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any
) {
  dispatch(setSuccess(payload));
  setTimeout(() => {
    dispatch(resetSuccess());
  }, 2000);
}
async function setErrorAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any
) {
  dispatch(setError(payload));
  setTimeout(() => {
    dispatch(resetError());
  }, 6000);
}

const initialState: AuthState = {
  user: undefined,
  loading: "idle",
  currentRequestId: undefined,
  fileUploadUri: undefined,
  error: null,
  success: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem("userInfo");
      state.user = undefined;
      googleLogout();
      //state.me = undefined;
    },
    resetSuccess: (state) => {
      state.success = null;
    },
    setSuccess: (state, { payload }) => {
      state.success = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      if (payload.message.includes("authorized")) {
        localStorage.removeItem("userInfo");
      }
    },
    resetError: (state) => {
      state.error = null;
    },
    // toggleDrawer: (state) => {
    //   state.drawerStatus = !state.drawerStatus
    // },
    // changePageTitle: (state, { payload }) => {
    //   state.pageTitle = payload
    // },
  },
  extraReducers: (builder) => {
    builder.addCase(signInApollo.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(signInApollo.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
    });
    builder.addCase(signInApollo.rejected, (state, { payload }) => {
      state.loading = "idle";
    });
    builder.addCase(signInRecaptcha.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(signInRecaptcha.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
    });
    builder.addCase(signInRecaptcha.rejected, (state, { payload }) => {
      state.loading = "idle";
    });
    builder.addCase(signUp.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(signUp.fulfilled, (state, { payload, meta }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(signUp.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(signUpRecaptcha.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(signUpRecaptcha.fulfilled, (state, { payload, meta }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(signUpRecaptcha.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(updateProfile.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(updateProfile.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
      state.fileUploadUri = undefined;
    });
    builder.addCase(updateProfile.rejected, (state, { payload }) => {
      state.loading = "idle";
    });
    builder.addCase(uploadProfilePicture.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(uploadProfilePicture.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      console.log(state.user);
      console.log(payload);
      if (state.user) state.user.avatar = payload.avatar as string;
    });
    builder.addCase(uploadProfilePicture.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(changePassword.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(changePassword.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(changePassword.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(forceChangePassword.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(forceChangePassword.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
    });
    builder.addCase(forceChangePassword.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(google.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(google.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
    });
    builder.addCase(google.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(facebook.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(facebook.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
    });
    builder.addCase(facebook.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(forgot.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(forgot.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(forgot.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(reset.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(reset.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(reset.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(verify.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(verify.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(verify.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(resend.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(resend.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(resend.rejected, (state, { payload }) => {
      state.loading = "idle";
    });

    builder.addCase(uploadFile.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(uploadFile.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.user = payload;
    });
    builder.addCase(uploadFile.rejected, (state, { payload }) => {
      state.loading = "idle";
    });
    builder.addCase(deleteFile.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(deleteFile.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = payload;
    });
    builder.addCase(deleteFile.rejected, (state, { payload }) => {
      state.loading = "idle";
    });
  },
});

// Auth selector
export const selectAuth = (state: RootState) => state.auth as AuthState;

export const { logout, setSuccess, resetSuccess, setError, resetError } =
  authSlice.actions;

export default authSlice.reducer;
